import {
    IsIn, IsInt, IsNumber, IsOptional, IsString, Max, ValidateNested, IsArray, ArrayNotEmpty, Length, ValidateIf, IsDefined, isDefined,
} from 'class-validator';

import { CrudPaginateRequest } from '../crud/crud-paginate.request';
import { maxIntValue } from '../utils/consts';

import { OrderStatusTxt } from './order-status';

/** Area production journal tab: classified by positions of the requested area, not by order.status */
export type OrderAreaJournalStatusTxt = 'pending' | 'work';

export interface OrderGetListRequestRaw {
    contrId?: number | string;
    orderId?: number | string;
    orderUuid?: string;
    userId?: number | string;
    areaId?: number | string;
    status?: OrderStatusTxt[];
    areaJournalStatus?: OrderAreaJournalStatusTxt;
    totalSum?: number | string;
    sumRange?: number | string;
    pickupAddress?: string;
    comment?: string;
    date?: Date | string;
    dateRange?: number;
    paginateInfo?: CrudPaginateRequest;
    assembleLocationFilter?: (string | null)[];
}

export class OrderGetListRequest {
    @IsOptional()
    @IsInt()
    public contrId?: number;

    @IsOptional()
    @IsInt()
    @Max(maxIntValue)
    public orderId?: number;

    @IsOptional()
    @IsString()
    @Length(1, 36)
    public orderUuid?: string;

    @IsOptional()
    @IsInt()
    public userId?: number;

    @ValidateIf((o: OrderGetListRequest) => isDefined(o.areaJournalStatus) || isDefined(o.areaId))
    @IsDefined({ message: '"areaId" is required when "areaJournalStatus" is defined' })
    @IsInt()
    public areaId?: number;

    @IsOptional()
    @IsArray()
    @ArrayNotEmpty()
    @IsIn(['new', 'pending', 'work', 'closed', 'shipped'] satisfies OrderStatusTxt[], { each: true })
    public status?: OrderStatusTxt[];

    /**
     * Filters orders by area position statuses (requires areaId):
     * - pending: has PENDING and no IN_WORK positions on the area
     * - work: has at least one IN_WORK position on the area
     */
    @IsOptional()
    @IsIn(['pending', 'work'] satisfies OrderAreaJournalStatusTxt[])
    public areaJournalStatus?: OrderAreaJournalStatusTxt;

    @IsOptional()
    @IsNumber()
    public totalSum?: number;

    @IsOptional()
    @IsInt()
    public sumRange?: number;

    @IsOptional()
    @IsString()
    public pickupAddress?: string;

    @IsOptional()
    @IsString()
    public comment?: string;

    @IsOptional()
    public date?: Date;

    @IsOptional()
    @IsInt()
    public dateRange?: number;

    @IsOptional()
    @IsArray()
    @ArrayNotEmpty()
    public assembleLocationFilter?: (string | null)[];

    @ValidateNested()
    public paginateInfo?: CrudPaginateRequest;

    constructor(data?: OrderGetListRequestRaw) {
        this.contrId = parseInt(data?.contrId as string, 10) || null;
        this.orderId = parseInt(data?.orderId as string, 10) || null;
        this.orderUuid = data?.orderUuid || null;
        this.userId = parseInt(data?.userId as string, 10) || null;
        this.areaId = parseInt(data?.areaId as string, 10) || null;
        this.status = data?.status;
        this.areaJournalStatus = data?.areaJournalStatus;
        this.totalSum = parseFloat(data?.totalSum as string) || null;
        this.sumRange = parseInt(data?.sumRange as string, 10) || null;
        this.pickupAddress = data?.pickupAddress;
        this.comment = data?.comment;
        this.date = typeof data?.date === 'string' ? new Date(data.date) : data?.date;
        this.dateRange = data?.dateRange;
        this.assembleLocationFilter = data?.assembleLocationFilter;
        /** @see CrudItemListRequest */
        this.paginateInfo = data?.paginateInfo && new CrudPaginateRequest(data.paginateInfo);
    }
}
